import OpenAI from 'openai';
import { tools, executeFunction } from './tools';
import { buildSystemPrompt } from './systemPrompt';

// Local storage key for OpenAI API key
export const OPENAI_API_KEY_STORAGE_KEY = 'paybyrd_openai_api_key';
export const OPENAI_CHAT_HISTORY_KEY = 'paybyrd_openai_chat_history';

/**
 * Get OpenAI client instance
 * @returns {OpenAI|null} OpenAI client instance or null if no API key is available
 */
export const getOpenAIClient = () => {
  const apiKey = localStorage.getItem(OPENAI_API_KEY_STORAGE_KEY);
  if (!apiKey) {
    return null;
  }

  return new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true, // Required for client-side usage
  });
};

/**
 * Get tools formatted for OpenAI
 * @returns {Array} Tools in OpenAI format
 */
export const getOpenAITools = () => {
  return tools.map(tool => ({
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters
    }
  }));
};

/**
 * Check if OpenAI API key is set
 * @returns {boolean} True if API key is set
 */
export const hasOpenAIApiKey = () => {
  return !!localStorage.getItem(OPENAI_API_KEY_STORAGE_KEY);
};

/**
 * Set OpenAI API key
 * @param {string} apiKey - OpenAI API key
 */
export const setOpenAIApiKey = (apiKey) => {
  localStorage.setItem(OPENAI_API_KEY_STORAGE_KEY, apiKey);
};

/**
 * Clear OpenAI API key
 */
export const clearOpenAIApiKey = () => {
  localStorage.removeItem(OPENAI_API_KEY_STORAGE_KEY);
};

/**
 * Get saved chat history from local storage for a specific store
 * @param {string} storeId - The store ID or 'global' if no store is selected
 * @returns {Array} Chat history messages
 */
export const getChatHistory = (storeId = 'global') => {
  try {
    const key = `${OPENAI_CHAT_HISTORY_KEY}_${storeId}`;
    const storedHistory = localStorage.getItem(key);
    if (storedHistory) {
      return JSON.parse(storedHistory);
    }
    
    // Fallback to global history if no store-specific history exists
    if (storeId !== 'global') {
      const globalHistory = localStorage.getItem(OPENAI_CHAT_HISTORY_KEY);
      if (globalHistory) {
        return JSON.parse(globalHistory);
      }
    }
  } catch (error) {
    console.error('Error retrieving chat history:', error);
  }

  return [];
};

/**
 * Save chat history to local storage for a specific store
 * @param {Array} history - Chat history to save
 * @param {string} storeId - The store ID or 'global' if no store is selected
 */
export const saveChatHistory = (history, storeId = 'global') => {
  try {
    // Limit history to last 20 messages to prevent localStorage size issues
    const limitedHistory = history.slice(-20);
    const key = `${OPENAI_CHAT_HISTORY_KEY}_${storeId}`;
    localStorage.setItem(key, JSON.stringify(limitedHistory));
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
};

/**
 * Clear chat history from local storage for a specific store
 * @param {string} storeId - The store ID or 'global' if no store is selected
 */
export const clearChatHistory = (storeId = 'global') => {
  const key = `${OPENAI_CHAT_HISTORY_KEY}_${storeId}`;
  localStorage.removeItem(key);
};

/**
 * Process OpenAI tool calls by executing the functions
 * @param {Array} toolCalls - Array of tool calls from OpenAI
 * @param {string} accessToken - Auth token for API authorization
 * @param {Object} selectedStore - Selected store for context
 * @returns {Promise<Array>} Tool results in OpenAI format
 */
const handleToolCalls = async (toolCalls, accessToken, selectedStore) => {
  return Promise.all(
    toolCalls.map(async (toolCall) => {
      try {
        const args = JSON.parse(toolCall.function.arguments);
        const response = await executeFunction(
          toolCall.function.name,
          args,
          accessToken,
          selectedStore
        );
        
        return {
          role: 'tool',
          tool_call_id: toolCall.id,
          content: response
        };
      } catch (error) {
        console.error(`Error handling tool call ${toolCall.function.name}:`, error);
        return {
          role: 'tool',
          tool_call_id: toolCall.id,
          content: JSON.stringify(`Error executing ${toolCall.function.name}: ${error.message || 'Unknown error'}`)
        };
      }
    })
  );
};

/**
 * Send message to OpenAI with conversation history
 * @param {string} message - User message
 * @param {Array} previousMessages - Previous messages in the conversation
 * @param {string} accessToken - Auth token for API authorization
 * @param {Object} selectedStore - Optional selected store for context
 * @returns {Promise<string>} AI response
 */
export const sendMessageToOpenAI = async (message, previousMessages = [], accessToken = null, selectedStore = null) => {
  const openai = getOpenAIClient();

  if (!openai) {
    throw new Error('OpenAI API key not set');
  }

  try {
    // Extract store info from message if it's in the format we're sending
    let userMessage = message;
    let extractedStore = selectedStore;

    // Check if message includes store context
    if (message.startsWith('Current selected store:')) {
      // Try to extract store ID from message format
      const storeMatch = message.match(/Current selected store: (.*?) \(ID: (.*?)\)/);
      if (storeMatch && storeMatch.length >= 3) {
        extractedStore = {
          label: storeMatch[1],
          value: storeMatch[2]
        };
        // Remove the store context from the message to avoid confusion
        const parts = message.split('\n\n');
        if (parts.length > 1) {
          userMessage = parts.slice(1).join('\n\n');
        }
      }
    }

    // Format previous messages for the API
    const formattedHistory = previousMessages
      .filter(msg => msg.text && msg.text.trim() !== '')
      .map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      }));

    // Build system prompt with store context, current date and timezone
    const systemPrompt = buildSystemPrompt(extractedStore);

    // Create API request with system prompt, history, and current message
    const messages = [
      {
        role: 'system',
        content: systemPrompt
      },
      ...formattedHistory,
      { role: 'user', content: userMessage }
    ];

    // Get the selected model or use default
    const modelName = localStorage.getItem('paybyrd_openai_model') || 'gpt-4-turbo';
    
    // Use the OpenAI API with tools
    const completion = await openai.chat.completions.create({
      model: modelName,
      messages: messages,
      temperature: 0.5,
      max_tokens: 1000,
      tools: getOpenAITools(),
    });

    const completionMessage = completion.choices[0].message;

    // If there are tool calls, handle them without including the initial response message
    if (completionMessage.tool_calls && completionMessage.tool_calls.length > 0) {
      // Create a copy of messages but don't include the initial completionMessage in what we send to the user
      // We only include the tool call itself in the message history for processing
      let updatedMessages = [...messages];
      
      // Add only the tool_calls part to the messages
      updatedMessages.push({
        role: 'assistant',
        tool_calls: completionMessage.tool_calls
      });

      // Process tool calls
      const toolResults = await handleToolCalls(completionMessage.tool_calls, accessToken, extractedStore);

      // Add tool results to conversation history
      updatedMessages = [...updatedMessages, ...toolResults];

      // Make another API call with the updated conversation using the same model
      const finalResponse = await openai.chat.completions.create({
        model: modelName,
        messages: updatedMessages,
        temperature: 0.7,
        max_tokens: 500,
      });

      // Return only the final response, not the initial one
      return finalResponse.choices[0]?.message?.content || 'Sorry, I could not generate a response.';
    }

    return completionMessage.content || 'Sorry, I could not generate a response.';
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    throw error;
  }
};